// models/commerce/Product.js - PRODUCTION READY
import { Model } from 'pinia-orm';
import Category from '@/models/commerce/Category.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import Variant from '@/models/commerce/Variant.js';

export default class Product extends Model {
  static entity = 'products';

  static fields() {
    return {
      id: this.uid(),
      name: this.attr(''),
      description: this.attr(''),
      slug: this.attr(''),
      avatarUrl: this.attr(''),
      basePrice: this.attr(0),
      postId: this.attr(''),
      categoryId: this.attr(''),

      // The two dated facts, product-scoped. Same contract and the same
      // must-be-declared-or-it-vanishes rule as Post.js — see the long note
      // there. The difference from a post: `availability` here reads every live
      // mark on THIS product, where a post unions the marks of all of its
      // products. Read through utils/productAvailability.js.
      availability: this.attr(null),
      lastCollected: this.attr(null),

      // Her own supplier tip for THIS product, and the club's answer to it
      // (BE→FE brief 2026-09-09). Same must-be-declared rule; omitted, never
      // null, when she has never named a shop. Byte-identical to
      // `supplierSuggestion` on an order line — one backend projection, so
      // utils/supplierSuggestion.js reads both and she gets one sentence about
      // one tip wherever she meets it.
      //
      // A tip she made against a LINE never appears here: this reads line-less
      // rows only, so the same tip cannot arrive twice under two receipts.
      mySuggestion: this.attr(null),

      // Relationships
      category: this.belongsTo(Category, 'categoryId'),
      variants: this.hasMany(Variant, 'productId'),
      orderBoxItems: this.hasMany(OrderBoxItem, 'productId'),

      createdAt: this.attr(''),
      updatedAt: this.attr(''),
    };
  }

  // ==========================================================================
  // BUSINESS LOGIC
  // ==========================================================================

  /**
     * Get active order items for this product
     * Note: Relationship loads automatically with find()
     */
  get activeOrderItems() {
    if (!this.orderBoxItems) return [];
    return this.orderBoxItems.filter(item => !item.isCancelled);
  }

  /**
     * Get total quantity ordered
     */
  get totalOrdered() {
    return this.activeOrderItems.reduce((sum, item) => sum + item.qty, 0);
  }

  /**
     * Get formatted base price
     */
  get formattedPrice() {
    return `${(this.basePrice / 100).toFixed(2)} EGP`;
  }

  /**
     * Check if product has variants
     */
  get hasVariants() {
    return this.variants && this.variants.length > 0;
  }

  /**
     * Get available variants (in stock)
     */
  get availableVariants() {
    if (!this.variants) return [];
    return this.variants.filter(v => v.isAvailable);
  }
}
